import { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { apiPost } from '../../api/axios';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

export default function MonthlyReport() {
  const [month, setMonth] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const res = await apiPost({
      action: 'habit_monthly_stats',
      params: {
        year: month.getFullYear(),
        month: month.getMonth() + 1,
      },
    });
    if (res.flag === 'success') {
      setStats(res.data);
      setError('');
    } else {
      setError(res.msg || 'Failed to load stats');
    }
    setLoading(false);
  }, [month]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const changeMonth = (delta) => {
    setMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const lineChartData = stats
    ? {
        labels: stats.dailyCounts.map((_, i) => i + 1),
        datasets: [
          {
            data: stats.dailyCounts,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59,130,246,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
        ],
      }
    : null;

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        cornerRadius: 12,
        callbacks: {
          label: (ctx) => ` ${ctx.raw} habit${ctx.raw !== 1 ? 's' : ''} completed`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: Math.max(...(stats?.dailyCounts || [0])) + 1,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { stepSize: 1 },
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Monthly Report</h2>
        <div className="flex items-center gap-2 bg-card-light dark:bg-card-dark backdrop-blur-md rounded-2xl px-4 py-2 border border-border-light dark:border-border-dark shadow-sm">
          <button onClick={() => changeMonth(-1)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-500 dark:text-slate-400 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-slate-900 dark:text-white min-w-[140px] text-center">{monthName}</span>
          <button onClick={() => changeMonth(1)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-500 dark:text-slate-400 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl px-4 py-3">
          <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <svg className="animate-spin h-10 w-10 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      ) : stats ? (
        <>
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-brand-600 to-brand-500 rounded-3xl shadow-lg shadow-brand-500/25 p-8 text-white">
            <p className="text-sm font-medium opacity-80">Monthly Summary</p>
            <div className="flex items-center justify-between mt-2">
              <h1 className="text-3xl font-bold">{(stats.successRate * 100).toFixed(0)}%</h1>
              <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                {stats.totalCompleted}/{stats.totalGoals} completed
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalCompleted}</p>
                <p className="text-xs opacity-80">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalGoals}</p>
                <p className="text-xs opacity-80">Goal</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.daysInMonth}</p>
                <p className="text-xs opacity-80">Days</p>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard title="Normalized Progress" value={`${(stats.normalized * 100).toFixed(1)}%`} />
            <MetricCard title="First Empty Day" value={stats.firstEmptyDay ? `Day ${stats.firstEmptyDay}` : 'None'} />
            <MetricCard title="Last 3 Days Avg" value={stats.last3DaysAvg.toFixed(1)} />
            <MetricCard title="Required Daily Avg" value={stats.requiredDailyAvg.toFixed(1)} />
          </div>

          {/* Daily Activity Chart */}
          <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-3xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Daily Activity</h3>
            <div className="h-64">
              {lineChartData && <Line data={lineChartData} options={lineOptions} />}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function MetricCard({ title, value }) {
  return (
    <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-2xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-4">
      <p className="text-xs text-slate-500 dark:text-slate-400">{title}</p>
      <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
    </div>
  );
}